import { useCallback, useEffect, useMemo, useState } from 'react';
import { avisar, avisarErro } from '../componentes/Avisos.tsx';
import { Confirmar } from '../componentes/Confirmar.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { FormularioProspeccao } from '../componentes/FormularioProspeccao.tsx';
import { FormularioContatoDaProspeccao } from '../componentes/FormularioContatoDaProspeccao.tsx';
import { ModelosDeAbordagem } from '../componentes/ModelosDeAbordagem.tsx';
import { JanelaEmBlocos } from '../componentes/PassoAPasso.tsx';
import { FormularioCliente } from '../componentes/FormularioCliente.tsx';
import {
  ErroDaApi,
  apiClientes,
  apiComercial,
  type CanalDoToque,
  type Cliente,
  type ContatoDaProspeccao,
  type Prospeccao,
  type SituacaoDaProspeccao,
} from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { paraTela } from '../lib/datas.ts';
import { ETAPAS, GRUPOS, MOTIVO_DO_INSUCESSO, NOME_DO_RETORNO, iniciais, nomeDaEtapa, oQueOFunilLeva, quandoFoi, quandoVence } from '../lib/funil.ts';
import '../estilos/sistema.css';
import '../estilos/comercial.css';

/**
 * ⭐⭐ A FICHA DA EMPRESA DO FUNIL, em página inteira — 20/09/2026 (PRD §49.10).
 *
 * > *"Na aba Comercial, ao clicar no nome do possível cliente, abre uma
 * > janelinha lateral que é horrível."* — Richard
 *
 * ⚠️ **O problema não era ser lateral: era o TAMANHO.** A gaveta cabia a ficha
 * de setembro — um decisor, três contatos, seis linhas de histórico. Com várias
 * pessoas por empresa, os oito textos de abordagem e um diário de conversa que
 * agora aceita 8.000 caracteres, ela virou um funil de papel dentro de 420
 * pixels. A ficha do cliente já é página inteira; esta passa a ser também, no
 * mesmo desenho e com a mesma navegação.
 *
 * ⛔ **Muda o desenho, não o que o botão faz:** mover etapa, registrar toque,
 * editar e apagar são as mesmas rotas, com as mesmas travas.
 */

const CANAIS: Array<{ id: CanalDoToque; nome: string }> = [
  { id: 'email', nome: 'E-mail' },
  { id: 'whatsapp', nome: 'WhatsApp' },
  { id: 'telefone', nome: 'Telefone' },
  { id: 'reuniao', nome: 'Reunião' },
  { id: 'outro', nome: 'Outro' },
];

const soDigitos = (v: string) => v.replace(/\D/g, '');

/** O canal como a pessoa o lê. ⚠️ Vem da mesma lista que o formulário usa. */
const nomeDoCanal = (id: string) => CANAIS.find((c) => c.id === id)?.nome.toLowerCase() ?? id;

/**
 * O vocativo como ele sai no e-mail: `Dr. Fulano`, e não `Dr. FULANO`.
 *
 * ⚠️ Sem nome, o vocativo é coletivo. ⭐ Desde 21/09/2026 os textos de
 * abordagem são todos coletivos, e esta função serve ao que a tela mostra de
 * quem decide na empresa, não mais ao e-mail.
 */
function vocativo(c: ContatoDaProspeccao | null): string {
  const primeiro = (c?.nome ?? '').trim().split(/\s+/)[0] ?? '';
  if (!primeiro) return 'Prezados';
  const proprio = primeiro.charAt(0) + primeiro.slice(1).toLocaleLowerCase('pt-BR');
  return `${c!.tratamento} ${proprio}`;
}

/** CNPJ e CPF saem formatados na tela e guardados só com dígitos (CLAUDE.md). */
function documentoNaTela(d: string): string {
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d;
}

export function FichaDaProspeccao({
  id,
  ehMaster,
  aoVoltar,
  aoAbrirCliente,
  aoExpirarSessao,
}: {
  id: string;
  ehMaster: boolean;
  aoVoltar: () => void;
  aoAbrirCliente: (id: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [p, setP] = useState<Prospeccao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [aApagar, setAApagar] = useState(false);
  const [contatoAberto, setContatoAberto] = useState<ContatoDaProspeccao | 'novo' | null>(null);
  const [contatoAApagar, setContatoAApagar] = useState<ContatoDaProspeccao | null>(null);
  /**
   * ⭐ **Qual e-mail está sendo marcado como devolvido** — 23/09/2026.
   *
   * ⚠️ **Pergunta o motivo antes de gravar**, e não é burocracia: *"endereço
   * não existe"* tira o endereço de circulação; *"caixa cheia"* é de hoje.
   * Sem a frase, os dois viram a mesma marca e a lista deixa de dizer o que
   * está errado nela.
   */
  const [emailQueVoltou, setEmailQueVoltou] = useState<ContatoDaProspeccao | null>(null);
  const [motivoDoRetorno, setMotivoDoRetorno] = useState('');
  const [vendoModelos, setVendoModelos] = useState(false);
  /**
   * ⭐⭐ **O canal e o texto do próximo passo — 13/10/2026, pergunta dele:**
   * *"Como eu saberei que o contato será pelo WhatsApp ou por e-mail? Como
   * saberei qual msg devo enviar?"* Quem decide é o servidor (`textoDoPasso`,
   * em `rotas/comercial.ts`), pela janela dos textos: duas cópias da regra
   * divergiriam na primeira mudança feita só numa delas.
   */
  const [textoDoPasso, setTextoDoPasso] = useState<{ id: string; canal: 'email' | 'whatsapp'; paraQuem: string } | null>(null);
  const [desvincular, setDesvincular] = useState(false);
  const [fechando, setFechando] = useState(false);
  /** ⭐ Clientes que já existem e parecem ser esta empresa — ver `clientesParecidos`. */
  const [parecidos, setParecidos] = useState<Cliente[] | null>(null);
  const [pedindoParaSair, setPedindoParaSair] = useState(false);
  const [desfazendoPedido, setDesfazendoPedido] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setP(await apiComercial.ver(id));
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro('Não consegui carregar esta empresa.');
    }
  }, [id, aoExpirarSessao]);

  const idDaEmpresa = p?.id;
  const passoDaEmpresa = p?.proximoPasso;
  const fechadaParaContato = !!p?.naoContatarEm;
  useEffect(() => {
    if (!idDaEmpresa || fechadaParaContato) return setTextoDoPasso(null);
    let vale = true;
    apiComercial
      .modelos(idDaEmpresa)
      .then((r) => {
        const m = r.modelos.find((x) => x.id === r.indicado);
        if (vale) setTextoDoPasso(m ? { id: m.id, canal: m.canal, paraQuem: m.paraQuem } : null);
      })
      .catch(() => vale && setTextoDoPasso(null));
    return () => {
      vale = false;
    };
  }, [idDaEmpresa, passoDaEmpresa, fechadaParaContato]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const principal = useMemo(() => p?.contatos.find((c) => c.principal) ?? p?.contatos[0] ?? null, [p]);

  async function mover(nova: SituacaoDaProspeccao) {
    if (!p) return;
    /* ⭐ Fechado não é só etapa: é o cliente nascendo. Só desvia quando ainda
       não há cliente ligado — reabrir convidaria a cadastrar o mesmo duas vezes. */
    if (nova === 'fechado' && !p.clienteId) {
      /*
       * ⭐⭐ **Antes do cadastro novo, o cliente que já existe — 29/09/2026.**
       * A Basalto preencheu o link de cadastro e entrou em Clientes; ao fechar,
       * o Hub abria o cadastro de um cliente NOVO, e o servidor recusaria o
       * mesmo CNPJ. Agora ele procura primeiro e oferece ligar ao que existe.
       * Sem achado, ou se a busca falhar, segue o cadastro de sempre.
       */
      const achados = await clientesParecidos(p).catch(() => []);
      return achados.length > 0 ? setParecidos(achados) : setFechando(true);
    }
    try {
      setP(await apiComercial.mover(p.id, nova));
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro('Não consegui mover a empresa de etapa.');
    }
  }

  if (erro) return <p className="com-vazio">{erro}</p>;
  if (!p) return <p className="com-vazio">Carregando…</p>;

  const lugar = [p.cidade && `${p.cidade}${p.uf ? `/${p.uf}` : ''}`, GRUPOS[p.grupo] ?? p.grupo].filter(Boolean).join(' · ');

  return (
    <div className="fp">
      <button type="button" className="fp-voltar" onClick={aoVoltar}>
        <Icone nome="voltar" tamanho={14} /> Comercial
      </button>

      <section className="s-folha s-identidade fp-identidade">
        <div className="s-monograma fp-monograma" aria-hidden="true">{iniciais(p.nome)}</div>
        <div className="fp-identidade-texto">
          <span className="s-sobrelinha">Empresa do funil</span>
          <h1 className="titulo">{p.nome}</h1>
          <p className="fp-meta">
            {lugar && <span>{lugar}</span>}
            {p.documento && (
              <span className="fp-doc">
                <span className="dado">{documentoNaTela(p.documento)}</span>
                <button
                  type="button"
                  className="fp-copiar"
                  title="Copiar o documento"
                  aria-label="Copiar o documento"
                  onClick={() => {
                    void navigator.clipboard
                      ?.writeText(documentoNaTela(p.documento))
                      .then(() => avisar('Documento copiado.'))
                      .catch(() => undefined);
                  }}
                >
                  <Icone nome="copiar" tamanho={13} />
                </button>
              </span>
            )}
            {p.perfil && <span className="fp-perfil">{p.perfil}</span>}
          </p>
        </div>
        <div className="fp-identidade-lado">
          <span className={`com-etapa etapa-${p.situacao}`}>{nomeDaEtapa(p.situacao)}</span>
          {/* ⛔ A letra do levantamento de 20/09 saiu daqui em 29/09/2026, junto
              com a coluna da lista: o e-mail é um só e ela não escolhe nada. */}
        </div>
      </section>

      {/*
        ⛔⛔ **A FAIXA DO PEDIDO — 21/09/2026.** Vem ANTES de tudo, e não num
        canto da lateral: quem abre a ficha precisa saber disso antes de pensar
        em escrever. ⚠️ Traz a data, o canal e a frase da pessoa — é o registro
        que sustenta o pedido se alguém perguntar depois.
      */}
      {p.naoContatarEm && (
        <section className="fp-nao-contatar">
          <Icone nome="atencao" tamanho={18} />
          <div>
            <h2>Esta empresa pediu para não ser procurada</h2>
            {/* ⚠️ Sem quebra de linha entre o texto e a vírgula: o JSX vira espaço antes dela. */}
            <p>
              {'Em '}
              <span className="dado">{paraTela(p.naoContatarEm.slice(0, 10))}</span>
              {p.naoContatarCanal ? `, por ${nomeDoCanal(p.naoContatarCanal)}` : ''}
              {p.naoContatarPor ? `, registrado por ${p.naoContatarPor.nome}` : ''}.
            </p>
            {p.naoContatarObs && <blockquote>{p.naoContatarObs}</blockquote>}
            <p className="fp-nao-contatar-regra">
              O Hub recusa registrar contato novo com ela e não monta mais os textos de abordagem. Ela saiu da
              fila e continua na base.
            </p>
          </div>
          {ehMaster && (
            <button type="button" className="imv-bt fantasma" onClick={() => setDesfazendoPedido(true)}>
              Desfazer
            </button>
          )}
        </section>
      )}

      {/*
        ⭐⭐ **O INSUCESSO — PRD §63.2, 22/10/2026.** Todo e-mail da empresa voltou, ou o domínio não
        recebe, ou recusa os nossos e-mails: ela saiu da fila. ⚠️ A saída é cadastrar outro contato
        com e-mail (a pesquisa achou outro caminho): o Hub tira a marca e marca o toque 1 para ele.
      */}
      {p.insucessoEm && !p.naoContatarEm && (
        <section className="fp-insucesso">
          <Icone nome="atencao" tamanho={18} />
          <div>
            <h2>Insucesso: {MOTIVO_DO_INSUCESSO[p.insucessoMotivo ?? ''] ?? 'sem e-mail que receba'}</h2>
            <p>
              {'Desde '}
              <span className="dado">{paraTela(p.insucessoEm.slice(0, 10))}</span>. A empresa saiu da fila
              {p.paraPesquisar ? ' e está na lista de pesquisa do Comercial, para achar outro e-mail.' : '.'}
            </p>
            <p className="fp-nao-contatar-regra">
              Um contato novo com e-mail tira a marca, e o Hub marca o e-mail de apresentação para ele.
            </p>
          </div>
        </section>
      )}

      {/*
        ⭐ OS DOMÍNIOS BLOQUEADOS DELA (§63.2 e §63.4, com os ajustes dos especialistas): "não
        contatar" o domínio é para sempre, mas desfaz-se quando foi engano (o "sair" que não era
        pedido, o 5.7.x que era falha nossa). Só o master desfaz, e fica gravado quem e quando.
      */}
      {!!p.dominiosBloqueados?.length && (
        <section className="fp-insucesso fp-dominios">
          <Icone nome="atencao" tamanho={18} />
          <div>
            <h2>{p.dominiosBloqueados.length === 1 ? 'Domínio bloqueado' : 'Domínios bloqueados'}</h2>
            <ul>
              {p.dominiosBloqueados.map((d) => (
                <li key={d.dominio}>
                  <span className="dado">{d.dominio}</span>
                  {' · '}
                  {d.motivo === 'sair' ? 'pediu para sair' : `recusou os nossos e-mails${d.codigo ? ` (${d.codigo})` : ''}`}
                  {' · '}
                  <span className="dado">{paraTela(d.criadoEm.slice(0, 10))}</span>
                  {ehMaster && (
                    <button
                      type="button"
                      className="com-desfazer"
                      onClick={() => {
                        void (async () => {
                          try {
                            await apiComercial.desfazerBloqueio(d.dominio);
                            avisar(`O bloqueio de ${d.dominio} foi desfeito.`);
                            setP(await apiComercial.ver(p.id));
                          } catch (e) {
                            avisarErro(e);
                          }
                        })();
                      }}
                    >
                      desfazer
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p className="fp-nao-contatar-regra">Nenhum e-mail sai para estes domínios, e o cadastro não aceita empresa nem contato deles.</p>
          </div>
        </section>
      )}

      <div className="fp-grade">
        <div className="fp-coluna">
          {/* ── a escada ─────────────────────────────────────────────── */}
          <section className="s-folha">
            <header className="s-folha-topo">
              <h2 className="s-folha-titulo">Etapa</h2>
              <p className="fp-nota">Mover é o trabalho do dia, e fica com data no histórico.</p>
            </header>
            <div className="s-folha-corpo">
              <div className="com-etapas">
                {ETAPAS.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    title={e.ajuda}
                    className={`com-etapa-botao etapa-${e.id}${p.situacao === e.id ? ' ativa' : ''}`}
                    onClick={() => void mover(e.id)}
                  >
                    {e.nome}
                  </button>
                ))}
              </div>
              {p.cliente && (
                <p className="com-virou">
                  <Icone nome="concluido" tamanho={14} />
                  Virou cliente:{' '}
                  <button type="button" className="com-link" onClick={() => aoAbrirCliente(p.cliente!.id)}>
                    {p.cliente.nome}
                  </button>
                  {ehMaster && (
                    <button type="button" className="com-desfazer" onClick={() => setDesvincular(true)}>
                      desfazer
                    </button>
                  )}
                </p>
              )}
            </div>
          </section>

          {/* ── com quem falar ───────────────────────────────────────── */}
          <section className="s-folha">
            <header className="s-folha-topo">
              <h2 className="s-folha-titulo">Com quem falar</h2>
              <button type="button" className="imv-bt fantasma" onClick={() => setContatoAberto('novo')}>
                <Icone nome="mais" tamanho={13} /> Novo contato
              </button>
            </header>
            <div className="s-folha-corpo">
              {p.contatos.length === 0 ? (
                <p className="fp-vazio">Nenhum contato cadastrado. O primeiro vira o principal.</p>
              ) : (
                <ul className="fp-contatos">
                  {p.contatos.map((c) => (
                    <li key={c.id} className={c.principal ? 'principal' : undefined}>
                      <div className="fp-contato-quem">
                        {c.nome ? (
                          <>
                            <strong>
                              {c.tratamento !== 'Prezados' && <span className="fp-trat">{c.tratamento}</span>} {c.nome}
                            </strong>
                            {c.cargo && <small>{c.cargo}</small>}
                          </>
                        ) : (
                          /* ⚠️ Sem nome não é contato incompleto: é o canal da
                             empresa, que várias só publicam. */
                          <strong className="fp-canal">Contato da empresa</strong>
                        )}
                        {c.principal && <span className="fp-principal">principal</span>}
                      </div>
                      <ul className="fp-canais">
                        {c.email && (
                          /* ⚠️ **O endereço morto continua legível, e riscado.**
                             Escondê-lo faria alguém redigitá-lo do site da
                             empresa na semana seguinte. */
                          <li className={c.voltouEm ? 'fp-email-voltou' : undefined}>
                            <Icone nome="email" tamanho={13} />
                            {c.voltouEm ? (
                              <span className="fp-email-morto">{c.email}</span>
                            ) : (
                              <a href={`mailto:${c.email}`}>{c.email}</a>
                            )}
                            {c.voltouEm && (
                              <span className="fp-voltou">
                                voltou em {c.voltouEm.slice(0, 10).split('-').reverse().join('/')}
                                {c.voltouMotivo ? ` · ${c.voltouMotivo}` : ''}
                              </span>
                            )}
                            {/* ⭐ PRD §63.2: o passageiro não sai; volta a tentar uma vez, na data. */}
                            {!c.voltouEm && c.retornoTipo === 'passageiro' && c.tentarDeNovoEm && (
                              <span className="fp-voltou fp-voltou-passageiro">
                                voltou ({NOME_DO_RETORNO.passageiro}{c.retornoCodigo ? `, ${c.retornoCodigo}` : ''}) · nova tentativa em{' '}
                                {paraTela(c.tentarDeNovoEm.slice(0, 10))}
                              </span>
                            )}
                          </li>
                        )}
                        {c.whatsapp && (
                          <li>
                            <Icone nome="telefone" tamanho={13} />
                            <a href={`https://wa.me/55${soDigitos(c.whatsapp)}`} target="_blank" rel="noreferrer">{c.whatsapp}</a>
                            <span className="fp-canal-nome">WhatsApp</span>
                          </li>
                        )}
                        {c.telefone && c.telefone !== c.whatsapp && <li><Icone nome="telefone" tamanho={13} />{c.telefone}</li>}
                        {c.linkedin && <li><Icone nome="globo" tamanho={13} /><a href={`https://${c.linkedin.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">LinkedIn</a></li>}
                      </ul>
                      {c.observacao && <p className="fp-contato-obs">{c.observacao}</p>}
                      <div className="fp-contato-acoes">
                        <button type="button" className="com-desfazer" onClick={() => setContatoAberto(c)}>editar</button>
                        {/* ⭐ Registrar é do colaborador; desfazer, do master. */}
                        {c.email && !c.voltouEm && (
                          <button
                            type="button"
                            className="com-desfazer"
                            onClick={() => {
                              setMotivoDoRetorno('');
                              setEmailQueVoltou(c);
                            }}
                          >
                            o e-mail voltou
                          </button>
                        )}
                        {c.voltouEm && ehMaster && (
                          <button
                            type="button"
                            className="com-desfazer"
                            onClick={() => {
                              void (async () => {
                                try {
                                  setP(await apiComercial.desfazerEmailVoltou(p.id, c.id));
                                } catch (e) {
                                  avisarErro(e);
                                }
                              })();
                            }}
                          >
                            não voltou
                          </button>
                        )}
                        {ehMaster && (
                          <button type="button" className="com-desfazer" onClick={() => setContatoAApagar(c)}>apagar</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* ⭐ O que vem, e quando — antes de registrar o que já foi. */}
          {!p.naoContatarEm && <ProximoPasso p={p} aoMudar={setP} aoExpirarSessao={aoExpirarSessao} />}

          {/* ⛔ Quem pediu para sair não recebe contato novo. O servidor recusa; aqui o campo nem aparece. */}
          {!p.naoContatarEm && <RegistrarContato p={p} aoMudar={setP} aoExpirarSessao={aoExpirarSessao} textoDoPasso={textoDoPasso} />}

          {/* ── o que já foi dito ────────────────────────────────────── */}
          <section className="s-folha">
            <header className="s-folha-topo">
              <h2 className="s-folha-titulo">O que já foi dito</h2>
              <p className="fp-nota">{p.toques.length === 0 ? 'nada ainda' : `${p.toques.length} contato(s)`}</p>
            </header>
            <div className="s-folha-corpo">
              {p.toques.length === 0 ? (
                <p className="fp-vazio">Nenhum contato registrado.</p>
              ) : (
                <ul className="fp-historico">
                  {p.toques.map((t) => (
                    <li key={t.id}>
                      <div className="fp-historico-topo">
                        <span className="com-canal">{CANAIS.find((c) => c.id === t.canal)?.nome ?? t.canal}</span>
                        {/* ⭐ PRD §63.3: a resposta e o e-mail que voltou não são contato que a casa fez. */}
                        {t.tipo === 'resposta' && <span className="fp-tipo-do-toque resposta">resposta</span>}
                        {t.tipo === 'devolucao' && <span className="fp-tipo-do-toque devolucao">voltou</span>}
                        {t.modelo && <span className={`com-modelo modelo-${t.modelo}`}>{t.modelo}</span>}
                        <span className="com-quando">{paraTela(t.aconteceuEm.slice(0, 10))}</span>
                        <span className="fp-quem">{t.criadoPor.nome}</span>
                      </div>
                      {t.observacao && <p>{t.observacao}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <aside className="fp-lado">
          {/* ── como escrever ────────────────────────────────────────── */}
          {!p.naoContatarEm && (
          <section className="s-folha">
            <header className="s-folha-topo">
              <h2 className="s-folha-titulo">Como escrever</h2>
            </header>
            <div className="s-folha-corpo">
              {/*
                ⭐⭐ **O E-MAIL É UM SÓ desde 21/09/2026**, e por isso aqui não
                há mais o que escolher nem vocativo que prever: o texto abre em
                "Prezados" e leva o nome da empresa dentro.

                ⛔ **A letra do levantamento saiu daqui.** Ela dizia qual dos
                seis textos usar; hoje não decide nada, e mostrá-la ao lado do
                botão faria a pessoa procurar uma escolha que não existe.
              */}
              <p className="fp-comoescrever">
                {textoDoPasso ? (
                  <>
                    <span className={`fp-canal-do-passo canal-${textoDoPasso.canal}`}>
                      {textoDoPasso.canal === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
                    </span>
                    <span>{textoDoPasso.paraQuem.charAt(0).toUpperCase() + textoDoPasso.paraQuem.slice(1)}.</span>
                  </>
                ) : (
                  <span>E-mail de apresentação, o mesmo para todas as empresas.</span>
                )}
              </p>
              <button type="button" className="imv-bt primario fp-largo" onClick={() => setVendoModelos(true)}>
                <Icone nome="copiar" tamanho={14} /> Ver e copiar o texto
              </button>
            </div>
          </section>
          )}

          {p.sinal && (
            <section className="s-folha">
              <header className="s-folha-topo"><h2 className="s-folha-titulo">Por que vale o contato</h2></header>
              <div className="s-folha-corpo"><p className="fp-texto">{p.sinal}</p></div>
            </section>
          )}
          {p.ressalva && (
            <section className="s-folha fp-ressalva">
              <header className="s-folha-topo"><h2 className="s-folha-titulo">Ressalva</h2></header>
              <div className="s-folha-corpo"><p className="fp-texto">{p.ressalva}</p></div>
            </section>
          )}

          <section className="s-folha">
            <header className="s-folha-topo"><h2 className="s-folha-titulo">A empresa</h2></header>
            <div className="s-folha-corpo">
              <dl className="s-props fp-props">
                {p.site && (
                  <div className="s-prop">
                    <dt>Site</dt>
                    <dd><a href={`https://${p.site.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">{p.site}</a></dd>
                  </div>
                )}
                {p.linkedin && (
                  <div className="s-prop">
                    <dt>LinkedIn</dt>
                    <dd><a href={`https://${p.linkedin.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">a página</a></dd>
                  </div>
                )}
                <div className="s-prop">
                  <dt>Cadastrada em</dt>
                  <dd className="dado">{paraTela(p.criadoEm.slice(0, 10))}</dd>
                </div>
                <div className="s-prop">
                  <dt>Último contato</dt>
                  <dd>{quandoFoi(p.ultimoContatoEm) ?? <i>nunca contatada</i>}</dd>
                </div>
                <div className="s-prop">
                  <dt>Última movimentação</dt>
                  <dd>{quandoFoi(p.ultimaMovimentacaoEm) ?? <i>nenhuma</i>}</dd>
                </div>
              </dl>
            </div>
          </section>

          {/*
            ⭐⭐ **Registrar o pedido é do COLABORADOR** — é o trabalho do dia,
            como mover o caso de situação, e não pode esperar autorização.
            ⛔ Desfazer é do master, e existe só para o clique errado.
          */}
          <div className="fp-acoes">
            {!p.naoContatarEm && (
              <button type="button" className="imv-bt fantasma fp-bt-sair" onClick={() => setPedindoParaSair(true)}>
                <Icone nome="atencao" tamanho={14} /> Pediu para não ser procurada
              </button>
            )}
            {ehMaster && (
              <>
                <button type="button" className="imv-bt fantasma" onClick={() => setEditando(true)}>
                  <Icone nome="lapis" tamanho={14} /> Editar cadastro
                </button>
                <button type="button" className="botao-apagar" onClick={() => setAApagar(true)}>
                  Apagar do funil
                </button>
              </>
            )}
          </div>
        </aside>
      </div>

      {editando && (
        <FormularioProspeccao
          inicial={p}
          aoFechar={() => setEditando(false)}
          aoSalvar={async (dados) => {
            const salva = await apiComercial.editar(p.id, dados);
            setEditando(false);
            setP(salva);
            if (salva.avisoDoDocumento) avisar(salva.avisoDoDocumento, 'ruim');
          }}
        />
      )}

      {contatoAberto && (
        <FormularioContatoDaProspeccao
          inicial={contatoAberto === 'novo' ? null : contatoAberto}
          primeiro={p.contatos.length === 0}
          aoFechar={() => setContatoAberto(null)}
          aoSalvar={async (dados) => {
            const salva =
              contatoAberto === 'novo'
                ? await apiComercial.criarContato(p.id, dados)
                : await apiComercial.editarContato(p.id, contatoAberto.id, dados);
            setContatoAberto(null);
            setP(salva);
          }}
        />
      )}

      {emailQueVoltou && (
        <JanelaEmBlocos
          rotulo="Comercial · qualidade da lista"
          sobrelinha="Comercial"
          titulo="Este e-mail voltou"
          sub="O endereço fica riscado na ficha e entra na conta de quantos voltaram."
          largura="estreita"
          aoFechar={() => setEmailQueVoltou(null)}
          rodape={
            <>
              <button type="button" className="imv-bt fantasma" onClick={() => setEmailQueVoltou(null)}>
                Cancelar
              </button>
              <span className="imv-esp" />
              <button
                type="button"
                className="imv-bt primario"
                onClick={() => {
                  void (async () => {
                    try {
                      setP(await apiComercial.emailVoltou(p.id, emailQueVoltou.id, { motivo: motivoDoRetorno }));
                      setEmailQueVoltou(null);
                    } catch (e) {
                      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
                      avisarErro(e);
                    }
                  })();
                }}
              >
                Registrar
              </button>
            </>
          }
        >
          {/*
            ⚠️ **A empresa CONTINUA na fila, e a tela precisa dizer isso.** Sem
            a frase, marcar um endereço morto parece desistir da empresa — e
            quem manda os e-mails deixaria de marcar para não perdê-la.
          */}
          <p className="fp-aviso-forte">
            <Icone nome="atencao" tamanho={15} />
            <span>
              <strong>{p.nome}</strong> continua na fila e na etapa em que está. Isto marca o{' '}
              <strong>endereço</strong>, não a empresa — resta falar por WhatsApp ou por outro e-mail.
            </span>
          </p>

          <p className="fp-nota">
            <span className="fp-email-morto">{emailQueVoltou.email}</span>
          </p>

          <label className="fp-campo">
            <span className="imv-rotulo">O que o servidor respondeu</span>
            <textarea
              rows={3}
              value={motivoDoRetorno}
              onChange={(e) => setMotivoDoRetorno(e.target.value)}
              placeholder="Endereço não existe · caixa cheia · domínio recusou…"
            />
            <small className="imv-nota">
              É o que separa o definitivo do temporário: endereço que não existe sai de circulação; caixa
              cheia pode voltar a funcionar amanhã.
            </small>
          </label>
        </JanelaEmBlocos>
      )}

      {contatoAApagar && (
        <Confirmar
          titulo="Apagar este contato?"
          descricao={
            contatoAApagar.principal
              ? `${contatoAApagar.nome || 'O contato da empresa'} sai da ficha. Como é o principal, o contato mais antigo que sobrar assume o lugar. Isso não se desfaz.`
              : `${contatoAApagar.nome || 'O contato da empresa'} sai da ficha. Isso não se desfaz.`
          }
          rotuloAcao="Apagar"
          aoConfirmar={async () => {
            try {
              setP(await apiComercial.apagarContato(p.id, contatoAApagar.id));
            } catch (e) {
              if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
              avisarErro('Não consegui apagar o contato.');
            }
          }}
          aoFechar={() => setContatoAApagar(null)}
        />
      )}

      {vendoModelos && <ModelosDeAbordagem prospeccaoId={p.id} aoFechar={() => setVendoModelos(false)} />}

      {pedindoParaSair && (
        <PedirParaNaoContatar
          p={p}
          aoMudar={setP}
          aoFechar={() => setPedindoParaSair(false)}
          aoExpirarSessao={aoExpirarSessao}
        />
      )}

      {desfazendoPedido && (
        <Confirmar
          titulo="Desfazer o pedido de não contatar?"
          descricao={`"${p.nome}" volta para a fila e o Hub aceita registrar contato de novo. O registro de quando e por onde a pessoa pediu some junto. ⛔ Isto é para o caso do clique errado, não para mudar de ideia sobre a empresa.`}
          rotuloAcao="Desfazer"
          aoConfirmar={async () => {
            try {
              setP(await apiComercial.desfazerNaoContatar(p.id));
              avisar('Pedido desfeito. A empresa voltou para a fila.');
            } catch (e) {
              if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
              avisarErro('Não consegui desfazer o pedido.');
            }
          }}
          aoFechar={() => setDesfazendoPedido(false)}
        />
      )}

      {desvincular && p.cliente && (
        <Confirmar
          titulo="Desfazer o vínculo com o cliente?"
          descricao={`"${p.nome}" deixa de apontar para ${p.cliente.nome}. O cliente não é apagado nem alterado, e a etapa continua onde está — quem move o funil é gente.`}
          rotuloAcao="Desfazer"
          aoConfirmar={async () => {
            try {
              setP(await apiComercial.desvincularCliente(p.id));
            } catch (e) {
              if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
              avisarErro('Não consegui desfazer o vínculo.');
            }
          }}
          aoFechar={() => setDesvincular(false)}
        />
      )}

      {parecidos && (
        <LigarAoClienteQueJaExiste
          p={p}
          clientes={parecidos}
          aoLigar={async (c) => {
            try {
              setP(await apiComercial.vincularCliente(p.id, c.id));
              avisar(`Ligada a ${c.nome}. A empresa foi para Fechado.`);
              setParecidos(null);
            } catch (e) {
              if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
              avisarErro(e instanceof ErroDaApi ? e.message : 'Não consegui ligar ao cliente.');
            }
          }}
          aoCadastrarOutro={() => {
            setParecidos(null);
            setFechando(true);
          }}
          aoFechar={() => setParecidos(null)}
        />
      )}

      {fechando && (
        <FormularioCliente
          inicial={null}
          partida={oQueOFunilLeva(p)}
          avisoDeAbertura={
            <span>
              Preenchido com o que o funil tem de <strong>{p.nome}</strong>. O nome comercial entrou em{' '}
              <strong>Nome fantasia</strong>; {p.documento ? 'o CNPJ veio do cadastro e a ' : 'digite o CNPJ e a '}
              <strong>razão social</strong> vem da Receita.
            </span>
          }
          aoFechar={() => setFechando(false)}
          aoSalvar={async (dados) => {
            const novo = await apiClientes.criar(dados);
            setP(await apiComercial.vincularCliente(p.id, novo.id));
            setFechando(false);
            aoAbrirCliente(novo.id);
          }}
        />
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar do funil?"
          descricao={`"${p.nome}" sai do funil, com os contatos e os toques registrados. Isso não se desfaz.`}
          rotuloAcao="Apagar"
          aoConfirmar={async () => {
            try {
              await apiComercial.apagar(p.id);
              aoVoltar();
            } catch {
              avisarErro('Não consegui apagar.');
            }
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
    </div>
  );
}

/**
 * ⭐⭐ O PRÓXIMO PASSO, na ficha — 21/09/2026 (PRD §49.17).
 *
 * > *"Negócio sem próximo passo marcado não é negócio em andamento."*
 *
 * ⚠️ **Nasce do toque e se edita à mão.** Registrado o e-mail, o Hub marca a
 * retomada para dali a cinco dias; escrever aqui **tira o passo das mãos dele**
 * e a cadência não reescreve mais nada — é a regra do motor do calendário.
 *
 * ⛔ **Passo sem data entra na ficha e não na fila.** A fila é por dia; recusar
 * o texto por não ter quando seria parede, porque há coisa que se anota antes
 * de ter data.
 */
function ProximoPasso({
  p,
  aoMudar,
  aoExpirarSessao,
}: {
  p: Prospeccao;
  aoMudar: (nova: Prospeccao) => void;
  aoExpirarSessao: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [passo, setPasso] = useState(p.proximoPasso);
  const [em, setEm] = useState(p.proximoPassoEm?.slice(0, 10) ?? '');
  const [salvando, setSalvando] = useState(false);

  const vence = quandoVence(p.proximoPassoEm);

  async function salvar() {
    setSalvando(true);
    try {
      aoMudar(await apiComercial.proximoPasso(p.id, { passo, ...(em ? { em } : {}) }));
      setEditando(false);
      avisar(passo ? 'Próximo passo marcado.' : 'Próximo passo limpo.');
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e instanceof ErroDaApi ? e.message : 'Não consegui marcar o próximo passo.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="s-folha">
      <header className="s-folha-topo">
        <h2 className="s-folha-titulo">Próximo passo</h2>
        {!editando && (
          <button
            type="button"
            className="imv-bt fantasma"
            onClick={() => {
              setPasso(p.proximoPasso);
              setEm(p.proximoPassoEm?.slice(0, 10) ?? '');
              setEditando(true);
            }}
          >
            <Icone nome="lapis" tamanho={13} /> {p.proximoPasso ? 'Mudar' : 'Marcar'}
          </button>
        )}
      </header>
      <div className="s-folha-corpo">
        {editando ? (
          <div className="fp-proximo-form">
            <label className="fp-campo">
              <span className="imv-rotulo">O que vem</span>
              <input
                type="text"
                value={passo}
                maxLength={200}
                onChange={(e) => setPasso(e.target.value)}
                placeholder="Retomada por WhatsApp"
              />
            </label>
            <label className="fp-campo">
              <span className="imv-rotulo">Quando</span>
              <input type="date" value={em} onChange={(e) => setEm(e.target.value)} />
              <small className="imv-nota">Sem data, fica na ficha e não entra na fila do dia.</small>
            </label>
            <div className="fp-proximo-acoes">
              <button type="button" className="imv-bt fantasma" onClick={() => setEditando(false)}>
                Cancelar
              </button>
              <button type="button" className="imv-bt primario" onClick={() => void salvar()} disabled={salvando}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        ) : p.proximoPasso ? (
          <p className={`fp-proximo${vence?.atrasado ? ' atrasado' : ''}`}>
            <strong>{p.proximoPasso}</strong>
            {vence && (
              <span>
                {vence.atrasado ? 'venceu ' : ''}
                {vence.texto}
                {p.proximoPassoEm && <> · {paraTela(p.proximoPassoEm.slice(0, 10))}</>}
              </span>
            )}
            {!p.proximoPassoEm && <span>sem data marcada</span>}
            {/* ⚠️ Dizer quem escreveu evita a pergunta "de onde saiu isto?". */}
            {p.proximoPassoDoHub && <i className="fp-proximo-origem">marcado pela cadência do Hub</i>}
          </p>
        ) : (
          <p className="fp-vazio">Nada marcado. Negócio sem próximo passo não é negócio em andamento.</p>
        )}
      </div>
    </section>
  );
}


/**
 * ⛔⛔ O PEDIDO DE NÃO CONTATAR — 21/09/2026 (PRD §49.16).
 *
 * > *"Quem pede para não ser procurado sai da lista na hora, e fica registrado
 * > que saiu."* — `docs/PROSPECCAO.md` §2.4
 *
 * ⚠️ **Pergunta antes**, como todo ato que não se desfaz por quem o faz: o
 * colaborador registra e não pode desfazer. ⛔ **E o canal é obrigatório** — sem
 * ele o registro não diz por onde a pessoa pediu, que é a primeira coisa que se
 * pergunta quando alguém duvida do pedido.
 */
function PedirParaNaoContatar({
  p,
  aoMudar,
  aoFechar,
  aoExpirarSessao,
}: {
  p: Prospeccao;
  aoMudar: (nova: Prospeccao) => void;
  aoFechar: () => void;
  aoExpirarSessao: () => void;
}) {
  const [canal, setCanal] = useState<CanalDoToque>('email');
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  usarDialogo(aoFechar);

  async function registrar() {
    setSalvando(true);
    setErro(null);
    try {
      aoMudar(await apiComercial.naoContatar(p.id, { canal, observacao }));
      avisar('Pedido registrado. A empresa saiu da fila.');
      aoFechar();
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof ErroDaApi ? e.message : 'Não consegui registrar o pedido.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <JanelaEmBlocos
      rotulo="Comercial · direito do destinatário"
      sobrelinha="Comercial"
      titulo="Esta empresa pediu para não ser procurada"
      sub="Fica registrado quando, por onde e o que a pessoa disse. A empresa sai da fila na hora."
      largura="estreita"
      aoFechar={aoFechar}
      erro={erro}
      rodape={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoFechar}>Cancelar</button>
          <span className="imv-esp" />
          <button type="button" className="imv-bt primario" onClick={() => void registrar()} disabled={salvando}>
            {salvando ? 'Registrando…' : 'Registrar o pedido'}
          </button>
        </>
      }
    >
      <p className="fp-aviso-forte">
        <Icone nome="atencao" tamanho={15} />
        <span>
          Depois disso, o Hub <strong>recusa</strong> registrar contato novo com <strong>{p.nome}</strong> e
          não monta mais os textos de abordagem para ela. A empresa continua na base, fora da fila. Só o
          master desfaz.
        </span>
      </p>

      <label className="fp-campo">
        <span className="imv-rotulo">Por onde a pessoa pediu</span>
        <select value={canal} onChange={(e) => setCanal(e.target.value as CanalDoToque)}>
          {CANAIS.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
      </label>

      <label className="fp-campo">
        <span className="imv-rotulo">O que a pessoa disse</span>
        <textarea
          rows={4}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="A frase dela, como chegou."
        />
        <small className="imv-nota">
          Guardar a frase é o que sustenta o registro se alguém perguntar depois.
        </small>
      </label>
    </JanelaEmBlocos>
  );
}


/**
 * ⭐ REGISTRAR CONTATO — 20/09/2026.
 *
 * > *"O campo 'Registrar contato' precisa ser melhor (atualmente é só uma linha
 * > que não dá pra escrever nada)."* — Richard
 *
 * ⚠️ **E ganhou a DATA**, que era a falta maior e não estava no pedido: quem
 * registra na segunda-feira o telefonema de sexta precisa dizer sexta, ou a
 * contagem de dias parados mente. ⛔ O servidor recusa data no futuro — isso
 * seria agenda, e agenda é outra coisa.
 */
function RegistrarContato({
  p,
  aoMudar,
  aoExpirarSessao,
  textoDoPasso,
}: {
  p: Prospeccao;
  aoMudar: (nova: Prospeccao) => void;
  aoExpirarSessao: () => void;
  /** ⭐ 13/10/2026: o canal do próximo passo já vem escolhido. */
  textoDoPasso: { id: string; canal: 'email' | 'whatsapp' } | null;
}) {
  const [canal, setCanal] = useState<CanalDoToque>('email');
  const canalDoPasso = textoDoPasso?.canal;
  useEffect(() => {
    if (canalDoPasso) setCanal(canalDoPasso);
  }, [canalDoPasso]);
  const [observacao, setObservacao] = useState('');
  const [quando, setQuando] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function registrar() {
    setSalvando(true);
    try {
      aoMudar(
        await apiComercial.registrarToque(p.id, {
          canal,
          observacao,
          ...(quando ? { aconteceuEm: quando } : {}),
        }),
      );
      setObservacao('');
      setQuando('');
      avisar('Contato registrado.');
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e instanceof ErroDaApi ? e.message : 'Não consegui registrar o contato.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="s-folha">
      <header className="s-folha-topo">
        <h2 className="s-folha-titulo">Registrar contato</h2>
        <p className="fp-nota">O que foi dito, e o que a pessoa respondeu.</p>
      </header>
      <div className="s-folha-corpo">
        <div className="fp-toque-linha">
          <label className="fp-campo">
            <span className="imv-rotulo">Canal</span>
            <select value={canal} onChange={(e) => setCanal(e.target.value as CanalDoToque)}>
              {CANAIS.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </label>
          <label className="fp-campo">
            <span className="imv-rotulo">Quando</span>
            <input type="date" value={quando} onChange={(e) => setQuando(e.target.value)} />
            <small className="imv-nota">{quando ? paraTela(quando) : 'vazio é hoje'}</small>
          </label>
          {canal === 'email' && (
            <p className="fp-toque-modelo">
              {textoDoPasso?.id === 'E2'
                ? 'Fica registrado que saiu a retomada por e-mail.'
                : 'Fica registrado que saiu o e-mail de apresentação.'}
            </p>
          )}
          {canal === 'whatsapp' && <p className="fp-toque-modelo">Fica registrado que saiu a mensagem de WhatsApp.</p>}
        </div>
        <textarea
          className="fp-toque-texto"
          rows={5}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          maxLength={8000}
          placeholder="O que foi dito, o que a pessoa respondeu, o que ficou combinado."
          aria-label="O que foi dito"
        />
        <div className="fp-toque-pe">
          <span className="imv-nota">{observacao.length > 7000 ? `${observacao.length} de 8.000` : ''}</span>
          <button type="button" className="imv-bt primario" disabled={salvando} onClick={() => void registrar()}>
            <Icone nome="check" tamanho={14} /> {salvando ? 'Registrando…' : 'Registrar'}
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * O nome da empresa no funil, pronto para procurar em Clientes: sem o que vem
 * entre parênteses ("BRUNO VANDERLEI (BVAA)") e sem o tipo societário, que o
 * cadastro da Receita escreve e o funil nem sempre.
 */
function nomeParaProcurar(nome: string): string {
  return nome
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\b(LTDA|S\/?A|EIRELI|ME|EPP)\.?$/i, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Os clientes que parecem ser esta empresa. ⚠️ **O documento manda:** com CNPJ
 * no funil, só o cadastro com o mesmo CNPJ conta. Sem ele, vale o nome, e
 * quem decide se é a mesma empresa é a pessoa, no diálogo. Nome curto demais
 * (menos de quatro letras) não procura: acharia metade da carteira.
 */
async function clientesParecidos(p: Prospeccao): Promise<Cliente[]> {
  if (p.documento) {
    const r = await apiClientes.conferirDocumento(p.documento);
    if (!r.existe || !r.cliente) return [];
    const { clientes } = await apiClientes.listar(p.documento);
    return clientes.filter((c) => c.id === r.cliente!.id);
  }
  const termo = nomeParaProcurar(p.nome);
  if (termo.length < 4) return [];
  const { clientes } = await apiClientes.listar(termo);
  return clientes.slice(0, 5);
}

function LigarAoClienteQueJaExiste({
  p,
  clientes,
  aoLigar,
  aoCadastrarOutro,
  aoFechar,
}: {
  p: Prospeccao;
  clientes: Cliente[];
  aoLigar: (c: Cliente) => Promise<void>;
  aoCadastrarOutro: () => void;
  aoFechar: () => void;
}) {
  const [ligando, setLigando] = useState<string | null>(null);
  usarDialogo(aoFechar);
  const um = clientes.length === 1;
  return (
    <JanelaEmBlocos
      rotulo="Comercial · fechar"
      sobrelinha="Comercial"
      titulo={um ? 'Esta empresa já está em Clientes' : 'Esta empresa pode já estar em Clientes'}
      sub={`Ligar ${p.nome} ao cadastro que já existe leva a empresa para Fechado, sem cadastrar de novo.`}
      largura="estreita"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoFechar}>Cancelar</button>
          <span className="imv-esp" />
          <button type="button" className="imv-bt" onClick={aoCadastrarOutro}>
            {um ? 'Não é esta: cadastrar outro' : 'Não é nenhuma: cadastrar outro'}
          </button>
        </>
      }
    >
      <ul className="fp-parecidos">
        {clientes.map((c) => (
          <li key={c.id}>
            <span className="fp-parecido-nome">
              <strong>{c.nome}</strong>
              {c.nomeFantasia && c.nomeFantasia !== c.nome && <span>{c.nomeFantasia}</span>}
              {c.documentoFormatado && <span className="dado">{c.documentoFormatado}</span>}
            </span>
            <button
              type="button"
              className="imv-bt primario"
              disabled={ligando !== null}
              onClick={async () => {
                setLigando(c.id);
                await aoLigar(c);
                setLigando(null);
              }}
            >
              {ligando === c.id ? 'Ligando…' : 'Ligar a este cliente'}
            </button>
          </li>
        ))}
      </ul>
    </JanelaEmBlocos>
  );
}
